import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Col,
  Row,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Spinner,
} from "reactstrap";

import classnames from "classnames";

import * as Yup from "yup";
import { useFormik } from "formik";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { createSelector } from "reselect";
import { partStatusData, categories } from "../../../common/data/olxCategories";

import AddImagesModal from "../../../Components/AddImagesModal";
import ImagePreview from "../../../Components/ImagePreview";

import { updatePart, publish } from "../../../slices/thunks";
import DetailsModal from "./Modals/DetailsModal";
import OlxInfoTab from "./Tabs/OlxTab";
import DetailsTab from "./Tabs/DetailsTab";

import NoteModal from "./Modals/NoteModal";
import {
  deletePartImage,
  featurePartImage,
} from "../../../helpers/fakebackend_helper";

const PartDetails = () => {
  document.title = "Detalji o dijelu | Sefer IMS";

  const dispatch = useDispatch();
  const [partDetails, setPartDetails] = useState(
    JSON.parse(sessionStorage.getItem("partDetails"))
  );
  const [customActiveTab, setCustomActiveTab] = useState("1");
  const toggleCustom = (tab) => {
    setCustomActiveTab(tab);
  };

  const user = JSON.parse(sessionStorage.getItem("authUser"));
  const isAdmin = user.role == "Admin" ? true : false;

  const selectProperties = createSelector(
    (state) => state.Part,
    (state) => ({
      parts: state.parts,
      currentPart: state.partDetails,
      error: state.error,
    })
  );

  const { currentPart, error } = useSelector(selectProperties);

  useEffect(() => {
    if (currentPart !== "") setPartDetails(currentPart);
  }, [currentPart]);

  const handleStatusChange = (status) => {
    if (status.value == 3) setNoteModal(true);
    else {
      const newstatus = {
        status: status.value,
      };
      const updatedPart = { ...partDetails, ...newstatus };
      dispatch(updatePart(updatedPart));
    }
  };

  const [editModal, setEditModal] = useState(false);
  const toggleEditModal = useCallback(() => {
    setEditModal(!editModal);
  }, [editModal]);

  const [noteModal, setNoteModal] = useState(false);
  const toggleNoteModal = useCallback(() => {
    setNoteModal(!noteModal);
  }, [noteModal]);

  const [addImagesModal, setAddImagesModal] = useState(false);
  const toggleAddImagesModal = useCallback(() => {
    setAddImagesModal(!addImagesModal);
  }, [addImagesModal]);

  const handlePublishClick = (partId) => {
    dispatch(publish({ id: partId, isVehicle: false }));
  };
  const handleCopyToClipboard = async () => {
    await navigator.clipboard.writeText(
      "https://olx.ba/artikal/" + partDetails.listingId
    );
    toast.info("Link kopiran.");
  };

  if (!partDetails) {
    return (
      <div className="page-content text-center">
        <Spinner color="primary" />
        <p>Detalji o dijelu se učitavaju, molimo pričekajte...</p>
      </div>
    );
  }

  const [images, setImages] = useState(partDetails?.images ?? []);
  const [featuredId, setFeaturedId] = useState(null);
  const [busyImageIds, setBusyImageIds] = useState(new Set());

  useEffect(() => {
    if (partDetails?.images) setImages(partDetails.images);
  }, [partDetails]);

  const withBusy = (id, fn) => async () => {
    setBusyImageIds((prev) => new Set(prev).add(id));
    try {
      await fn();
    } finally {
      setBusyImageIds((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    }
  };

  const onFeature = (img) =>
    withBusy(img.id, async () => {
      try {
        await featurePartImage(partDetails.id, img.id);
        setFeaturedId(img.id); // UI highlight
        // optionally: toast.success("Glavna slika postavljena.");
      } catch (e) {
        // toast.error(e?.toString() ?? "Neuspješno postavljanje glavne slike.");
      }
    })();

  const onDelete = (img) =>
    withBusy(img.id, async () => {
      console.log("Deleting image", img.id, "for part", partDetails.id);
      if (!window.confirm("Obrisati ovu sliku?")) return;
      try {
        await deletePartImage(partDetails.id, img.id);
        setImages((prev) => prev.filter((i) => i.id !== img.id));
        if (featuredId === img.id) setFeaturedId(null);
        toast.info("Slika obrisana.");
      } catch (e) {
        console.error("Delete failed:", e);
        toast.error(e?.toString() ?? "Neuspješno brisanje slike.");
      }
    })();

  return (
    <div className="page-content">
      <Container fluid>
        <ToastContainer closeButton={false} limit={3} />
        <DetailsModal
          partDetails={partDetails}
          show={editModal}
          onCloseClick={toggleEditModal}
        />
        <NoteModal
          partDetails={partDetails}
          show={noteModal}
          onCloseClick={toggleNoteModal}
        />
        <AddImagesModal
          item={{
            name: partDetails.name,
            id: partDetails.id,
            contentDirectory: partDetails.contentDirectory,
            isPart: true,
          }}
          show={addImagesModal}
          onCloseClick={toggleAddImagesModal}
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Row className="gx-lg-5">
                  <Col xl={4} md={8} className="mx-auto">
                    <ImagePreview
                      images={images}
                      featuredId={featuredId}
                      onFeature={onFeature}
                      onDelete={onDelete}
                      busyImageIds={busyImageIds}
                    />
                  </Col>

                  <Col xl={8}>
                    <div className="mt-xl-0 mt-5">
                      <div className="d-flex">
                        <div className="flex-grow-1 gap-3">
                          <div className="hstack gap-3 flex-wrap">
                            <h2>
                              {partDetails.publicIdentifier +
                                " " +
                                partDetails.name}
                            </h2>
                            <h4>
                              <span
                                className={
                                  "badge rounded-pill border border-" +
                                  partStatusData[partDetails.status - 1]
                                    .badgeType +
                                  " text-" +
                                  partStatusData[partDetails.status - 1]
                                    .badgeType
                                }
                              >
                                {" " +
                                  partStatusData[partDetails.status - 1].label}
                              </span>
                              {partDetails.listingId != null && (
                                <span className="mx-1 badge rounded-pill border border-info text-uppercase text-info">
                                  {" "}
                                  OLX{" "}
                                </span>
                              )}
                            </h4>
                          </div>
                          {isAdmin && (
                            <div>
                              <h5 className="mt-4">
                                Cijena:{" "}
                                {partDetails.sellingPrice == null ||
                                partDetails.sellingPrice == 0
                                  ? "Nije uneseno!"
                                  : partDetails.sellingPrice + " KM"}
                              </h5>
                              {partDetails.status != 2 && (
                                <div className="hstack gap-3 flex-wrap">
                                  <h5> Promijeni status: </h5>
                                  {partStatusData
                                    .filter(
                                      (status) =>
                                        status.value != partDetails.status &&
                                        status.value != 2
                                    )
                                    .map((status, key) => (
                                      <Button
                                        className="mx-2"
                                        color={status.badgeType}
                                        outline
                                        onClick={() => {
                                          handleStatusChange(status);
                                        }}
                                        key={key}
                                      >
                                        {status.label}
                                      </Button>
                                    ))}
                                </div>
                              )}
                              {partDetails.status == 3 && partDetails.note?.description && (
                                <h4 className="mt-3">
                                  {"Bilješka: " + partDetails.note.description}
                                </h4>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="hstack gap-2 justify-content-end d-print-none">
                          <button
                            id="edit-btn"
                            type="button"
                            className="btn btn-light"
                            onClick={() => {
                              toggleEditModal();
                            }}
                          >
                            <i className="ri-pencil-fill" />
                          </button>
                        </div>
                      </div>
                      {partDetails.listingId != null && (
                        <div className="mt-4">
                          <span className="mx-2 fw-semibold fs-16 text-uppercase">
                            Olx Link:
                          </span>
                          <span className="mx-2 fw-semibold fs-16">
                            {"https://olx.ba/artikal/" + partDetails.listingId}
                          </span>
                          <span
                            className="btn btn-icon btn-outline-info mx-2 text-primary"
                            style={{ cursor: "pointer" }}
                            onClick={handleCopyToClipboard}
                          >
                            <i className="ri-clipboard-line fs-16" />
                          </span>
                        </div>
                      )}
                      {isAdmin &&
                        partDetails.listingId == null &&
                        partDetails.status == 1 && (
                          <div className="hstack justify-content-end">
                            <Button
                              color="primary"
                              outline
                              onClick={() => {
                                handlePublishClick(partDetails.id);
                              }}
                            >
                              Objavi na OLX
                            </Button>
                          </div>
                        )}
                      <div className="product-content mt-5">
                        <Nav tabs className="nav-tabs-custom nav-success">
                          <NavItem>
                            <NavLink
                              style={{ cursor: "pointer" }}
                              className={classnames({
                                active: customActiveTab === "1",
                              })}
                              onClick={() => {
                                toggleCustom("1");
                              }}
                            >
                              {" "}
                              Detalji{" "}
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              style={{ cursor: "pointer" }}
                              className={classnames({
                                active: customActiveTab === "2",
                              })}
                              onClick={() => {
                                toggleCustom("2");
                              }}
                            >
                              {" "}
                              Opis{" "}
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              style={{ cursor: "pointer" }}
                              className={classnames({
                                active: customActiveTab === "3",
                              })}
                              onClick={() => {
                                toggleCustom("3");
                              }}
                            >
                              {" "}
                              OLX{" "}
                            </NavLink>
                          </NavItem>
                        </Nav>
                        <TabContent
                          id="nav-tabContent"
                          className="border border-top-0 p-4"
                          activeTab={customActiveTab}
                        >
                          <TabPane id="nav-speci" tabId="1">
                            <DetailsTab partDetails={partDetails} />
                          </TabPane>
                          <TabPane id="nav-speci" tabId="2">
                            <div className="mt-4 text-muted">
                              <h5 className="fs-13">Opis:</h5>
                              <p>
                                {partDetails.description != null &&
                                partDetails.description.length > 0
                                  ? partDetails.description
                                  : "Za ovaj artikal nije unesen opis."}
                              </p>
                            </div>
                          </TabPane>
                          <TabPane id="nav-speci" tabId="3">
                            <OlxInfoTab />
                          </TabPane>
                        </TabContent>
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PartDetails;
